import { useMemo, useState, useRef } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useTeamCoursesSummary } from '../../../../lib/api/swrApi';
import Table from '../../base/Table/Table';
import { adaptArrayToTableObject } from '../../base/TableManager/TableManager.utils';
import TrainingSummaryView from '../../base/TrainingSummaryView/TrainingSummaryView';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import {
  adaptCourseSummariesToTableData,
  assignedCoursesSortValues,
  dataToSummaryTiles,
} from './TeamAssignedTrainings.utils';
import { withInternalComponents } from '../../../../lib/context/InternalComponentContext';
import { useId } from '../../../../lib/hooks/useId';
import styles from './TeamAssignedTrainings.module.scss';
import { TeamAssignedTrainingsResultsProps } from './TeamAssignedTrainings.types';
import { TEAM_ASSIGNED_TRAININGS_HEADINGS } from '../../../../lib/constants/courses';
import { SEARCH_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import SkeletonTrainingSummaryView from '../../base/TrainingSummaryView/SkeletonTrainingSummaryView';
import { isNil } from '../../../../lib/utils/isNil';
import ErrorInfo from '../../base/ErrorInfo/ErrorInfo';
import useFeature from '../../../../lib/hooks/useFeature';
import { FeatureToggleCodename } from '../../../../lib/types/enums';

const TeamAssignedTrainingsResults = ({
  teamId,
  state,
  dispatch,
  isTeamsLoading,
  summaryItemsOrder,
  ...rest
}: TeamAssignedTrainingsResultsProps) => {
  const previousCall = useRef<AbortController | undefined>(undefined);
  const [phrase, setPhrase] = useState<string>(state.search);
  const hideOverdue = useFeature(FeatureToggleCodename.hideOverdueDates);
  const tableHeadings = TEAM_ASSIGNED_TRAININGS_HEADINGS.filter(
    headings => !hideOverdue || headings !== 'totalOverdue',
  );
  const sortValues = assignedCoursesSortValues(tableHeadings);

  const getSignal = () => {
    if (previousCall.current) {
      previousCall.current.abort();
    }
    previousCall.current = new AbortController();

    return previousCall.current.signal;
  };

  const { data, isLoading, error } = useTeamCoursesSummary({
    teamid: teamId ?? '',
    query: state,
    shouldFetch: !!teamId,
    getSignal,
  });
  const { Meta, CourseSummaries, Summary } = data ?? {};
  const tilesId = useId('tile-');

  const tableData = useMemo(
    () =>
      CourseSummaries?.map(adaptCourseSummariesToTableData(!!hideOverdue)).reduce(
        adaptArrayToTableObject(tableHeadings),
        {},
      ),
    [data],
  );

  const selectedRows = useMemo(() => {
    if (!CourseSummaries || hideOverdue) {
      return [];
    }

    return Object.values(CourseSummaries)
      .filter(({ totalOverdue }) => totalOverdue)
      .map(({ id }) => id ?? '');
  }, [tableData]);

  const updateDebouncedSearch = useDebouncedCallback(
    (newPhrase: string) => dispatch({ type: 'phraseChange', payload: { phrase: newPhrase } }),
    SEARCH_DEBOUNCE_TIME,
  );

  const handlePhraseChange = (newPhrase: string) => {
    setPhrase(newPhrase);
    updateDebouncedSearch(newPhrase);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    dispatch({ type: 'sortChange', payload: { sortValue: event.target.value } });
  };

  const TrainingSummarySection =
    (isNil(data) && !!teamId) || isTeamsLoading ? (
      <SkeletonTrainingSummaryView disableHeader />
    ) : (
      <TrainingSummaryView
        classNames={{ root: styles.trainingSummary, course: styles.course }}
        tiles={dataToSummaryTiles(tilesId, Summary, summaryItemsOrder)}
      />
    );

  if (error) {
    return <ErrorInfo error={error} internal />;
  }

  return (
    <Table
      className={styles.table}
      selected={selectedRows}
      sortValues={sortValues}
      activeSortValue={`${state.sortBy}|${state.sortDirection}`}
      activePage={Meta?.page ?? 0}
      totalPages={Meta?.totalPages ?? 1}
      handleSortChange={handleSortChange}
      handlePageChange={page => dispatch({ type: 'pageChange', payload: { page } })}
      handleItemsOnPage={pageSize => dispatch({ type: 'pageSizeChange', payload: { pageSize } })}
      headings={['name.course', ...tableHeadings.slice(1)]}
      data={tableData ?? {}}
      dataOrder={CourseSummaries?.map(({ id }) => id ?? '') ?? []}
      search={{ phrase, handleChange: handlePhraseChange }}
      {...rest}
      pageSize={state?.pageSize ?? rest.pageSize}
      isInitialLoad={isTeamsLoading || (isNil(data) ? isLoading : false)}
      isLoading={isNil(data) ? false : isLoading}
      disableBlurring
    >
      {TrainingSummarySection}
    </Table>
  );
};

export default withInternalComponents(TeamAssignedTrainingsResults);
